import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styles from './OfferWizardModal.module.css';
import OfferDetailsForm from './OfferDetailsForm';
import OfferPreview from './OfferPreview';
import { selectOrganizationId } from '../../store/selectors/authSelectors';
import { InterviewItem, GeneratedOffer } from './types';
import { ATS_CONSTANTS } from './constants';
import { useOfferSchema } from './hooks/useOfferSchema';
import { useOfferGeneration } from './hooks/useOfferGeneration';
import ErrorModal from '../../components/Modals/ErrorModal';
import { useSnackbar } from '../../contexts/SnackbarContext';

interface OfferWizardModalProps {
  isOpen: boolean;
  mode?: 'single' | 'bulk' | string;
  candidates?: InterviewItem[];
  jobTitle?: string;
  jobId?: string;
  outletId?: string;
  onClose: () => void;
  onSubmitOffers: (generatedOffers: GeneratedOffer[]) => Promise<boolean | void | { success: boolean; failedIds: string[] }>;
}

const OfferWizardModal: React.FC<OfferWizardModalProps> = ({
  isOpen,
  mode = ATS_CONSTANTS.OFFER_MODES.SINGLE,
  candidates = [],
  jobTitle,
  jobId,
  outletId,
  onClose,
  onSubmitOffers,
}) => {
  const [step, setStep] = useState(1);
  const [formValues, setFormValues] = useState<any>(null);
  const [errorModal, setErrorModal] = useState<{ isOpen: boolean; message: string }>({
    isOpen: false,
    message: '',
  });
  const organizationId = useSelector(selectOrganizationId);
  const { showSnackbar } = useSnackbar();

  const { schema, htmlTemplate, loading: schemaLoading, error: schemaError } = useOfferSchema(
    organizationId || undefined,
    isOpen,
    outletId,
    jobId
  );

  const offersPreview = useOfferGeneration(candidates, formValues, htmlTemplate, jobTitle);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setFormValues(null);
      setErrorModal({ isOpen: false, message: '' });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFormSubmit = (vals: any) => {
    setFormValues(vals);
    setStep(2); // Move to preview step
  };

  const handleFinalSubmit = async (generatedOffers: GeneratedOffer[]) => {
    try {
      const result = await onSubmitOffers(generatedOffers);

      if (result && typeof result === 'object' && 'success' in result) {
        const typedResult = result as { success: boolean; failedIds: string[] };
        if (!typedResult.success || (typedResult.failedIds && typedResult.failedIds.length > 0)) {
          const failedNames = typedResult.failedIds && typedResult.failedIds.length > 0
            ? typedResult.failedIds.map(id => candidates.find(c => c.id === id)?.name || id).join(', ')
            : 'Unknown error occurred';

            setErrorModal({
              isOpen: true,
              message: typedResult.failedIds && typedResult.failedIds.length > 0
                ? `Offer generation failed for: ${failedNames}. Please try again.`
                : 'Failed to generate offer letters. Please try again.'
            });
            return;
          }
      }
      onClose();
    } catch (error: any) {
      console.error('[OfferWizardModal] Failed to submit offers', error);
      showSnackbar(error?.message || 'Failed to submit offers. Please try again.', 'error');
    }
  };

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.modal} data-testid="ats-offer-wizard-modal">
        <div className={styles.header}>
          <div className={styles.title}>
            Generate Offer {mode === ATS_CONSTANTS.OFFER_MODES.BULK ? `(${candidates.length})` : ''}
          </div>
          <button className={styles.close} onClick={onClose} data-testid="offer-wizard-close">
            ×
          </button>
        </div>
        <div className={styles.content}>
          {schemaLoading && (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p>Loading offer form...</p>
            </div>
          )}
          {schemaError && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#b3261e' }}>
              <p>{schemaError}</p>
              <button onClick={onClose} style={{ marginTop: '10px' }}>
                Close
              </button>
            </div>
          )}
          {!schemaLoading && !schemaError && schema && step === 1 && (
            <OfferDetailsForm
              key={mode}
              mode={mode}
              candidates={candidates}
              jobTitle={jobTitle}
              schema={schema}
              onCancel={onClose}
              onSubmit={handleFormSubmit}
            />
          )}
          {!schemaLoading && !schemaError && step === 2 && offersPreview.length > 0 && (
            <OfferPreview
              offersPreview={offersPreview}
              mode={mode}
              candidates={candidates}
              onBack={() => setStep(1)}
              onGenerateAndSave={handleFinalSubmit}
            />
          )}
        </div>
      </div>
      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
      />
    </>
  );
};

export default OfferWizardModal;
